import React, { useEffect, useRef, useState } from 'react';
import { Box, FormLabel, SimpleGrid, Text, VStack } from '@chakra-ui/react';
import { AppInput } from './AppInput';
import VillageMap from '../map/VillageMap';
import { reverseGeocode } from '../../utils/geocode';

/**
 * Manual address (#46 order) + select-on-map (#52).
 * Map pin stays centered while dragging; reverse-geocode fills fields (like mobile).
 */
export function ManualAddressWithMap({
  form,
  onChange,
  mapOpen = true,
  showPinOverride = false,
  pinOverride,
  onPinOverrideChange,
}) {
  const lat = form.latitude;
  const lng = form.longitude;
  const [geocoding, setGeocoding] = useState(false);
  const [geoError, setGeoError] = useState('');
  const debounceRef = useRef(null);
  const seqRef = useRef(0);

  useEffect(
    () => () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    },
    []
  );

  const handlePick = (pickedLat, pickedLng) => {
    onChange({
      latitude: pickedLat,
      longitude: pickedLng,
    });
    setGeoError('');

    if (debounceRef.current) clearTimeout(debounceRef.current);
    const seq = ++seqRef.current;
    // Debounce like mobile — fires after drag pauses (~400ms), including mid-pan.
    debounceRef.current = setTimeout(async () => {
      setGeocoding(true);
      try {
        const place = await reverseGeocode(pickedLat, pickedLng);
        if (seq !== seqRef.current) return;
        onChange({
          latitude: pickedLat,
          longitude: pickedLng,
          address1: place.address1 || '',
          address2: place.address2 || '',
          locality: place.locality || '',
          taluka: place.taluka || '',
          district: place.district || '',
          state: place.state || '',
          country: place.country || 'India',
          pin: place.pin || '',
        });
      } catch (err) {
        if (seq !== seqRef.current) return;
        setGeoError(err?.message || 'Could not resolve that map pin');
      } finally {
        if (seq === seqRef.current) setGeocoding(false);
      }
    }, 400);
  };

  return (
    <VStack align="stretch" spacing={4}>
      <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
        <AppInput
          label="House"
          placeholder="House / building"
          value={form.address1 || ''}
          onChange={(e) => onChange({ address1: e.target.value })}
          maxLength={120}
        />
        <AppInput
          label="Street"
          placeholder="Street / road"
          value={form.address2 || ''}
          onChange={(e) => onChange({ address2: e.target.value })}
          maxLength={120}
        />
      </SimpleGrid>

      <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
        <AppInput
          label="Village"
          placeholder="Village / locality"
          value={form.locality || ''}
          onChange={(e) => onChange({ locality: e.target.value })}
          maxLength={120}
        />
        <AppInput
          label="Taluka"
          placeholder="Taluka / tehsil"
          value={form.taluka || ''}
          onChange={(e) => onChange({ taluka: e.target.value })}
          maxLength={120}
        />
      </SimpleGrid>

      <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
        <AppInput
          label="Dist"
          placeholder="District"
          value={form.district || ''}
          onChange={(e) => onChange({ district: e.target.value })}
          maxLength={120}
        />
        <AppInput
          label="State"
          placeholder="State"
          value={form.state || ''}
          onChange={(e) => onChange({ state: e.target.value })}
          maxLength={120}
        />
      </SimpleGrid>

      <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
        <AppInput
          label="PIN"
          placeholder="6-digit PIN"
          value={form.pin || ''}
          onChange={(e) =>
            onChange({ pin: e.target.value.replace(/\D/g, '').slice(0, 6) })
          }
          maxLength={6}
        />
        {showPinOverride ? (
          <AppInput
            label="PIN override"
            placeholder="Optional site override"
            value={pinOverride || ''}
            onChange={(e) =>
              onPinOverrideChange?.(e.target.value.replace(/\D/g, '').slice(0, 6))
            }
            maxLength={6}
          />
        ) : (
          <Box />
        )}
      </SimpleGrid>

      <Box>
        <FormLabel mb={2}>Select on map</FormLabel>
        <Text fontSize="13px" color="text.secondary" mb={2}>
          Move the map to set the pin (stays centered). Address updates when you
          pause dragging (~0.4s). Tap to jump without zooming.
        </Text>
        {mapOpen ? (
          <VillageMap
            latitude={lat}
            longitude={lng}
            clickable
            height="260px"
            hint="Drag to move · tap to re-center · scroll wheel zoom is off so the dialog can scroll."
            onPick={handlePick}
          />
        ) : null}
        {geocoding ? (
          <Text fontSize="12px" color="text.secondary" mt={2}>
            Looking up address…
          </Text>
        ) : null}
        {geoError ? (
          <Text fontSize="12px" color="red.600" mt={2}>
            {geoError}
          </Text>
        ) : null}
        {lat != null && lng != null ? (
          <Text fontSize="12px" color="text.secondary" mt={2}>
            Pin {Number(lat).toFixed(5)}, {Number(lng).toFixed(5)}
          </Text>
        ) : null}
      </Box>
    </VStack>
  );
}
